import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FileText, Mail, ShieldCheck, CreditCard, AlertCircle, Users } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'
import { legalCss } from './PrivacyPolicy'

const B = 'https://pixner.net/bankio/main/assets/images'

const sections = [
  { n: '01', title: 'Acceptance of Terms', body: <p className="legal-p">By creating an account on A1XPAY or using our payment services, APIs, or website, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.</p> },
  { n: '02', title: 'Eligibility', body: <p className="legal-p">You must be at least 18 years of age and a resident of India to use A1XPAY services. Businesses must be validly registered under applicable Indian law. We reserve the right to verify eligibility at any time.</p> },
  { n: '03', title: 'Account Registration and KYC', body: <ul className="legal-ul"><li>You must provide accurate and complete information during registration.</li><li>You are required to complete KYC verification before processing live transactions.</li><li>You are responsible for maintaining the confidentiality of your account credentials.</li><li>You must notify us immediately if you suspect unauthorized access to your account.</li></ul> },
  { n: '04', title: 'Permitted Use', body: <p className="legal-p">A1XPAY services may only be used for lawful business purposes. You may not use our platform to process payments for prohibited businesses including but not limited to illegal goods, gambling without proper license, adult content, or any activity prohibited by RBI or applicable Indian law.</p> },
  { n: '05', title: 'Transaction Processing and Settlements', body: <ul className="legal-ul"><li>All transactions are subject to our fraud detection and risk management systems.</li><li>We reserve the right to hold settlements in cases of suspected fraud or unusual activity.</li><li>Settlement timelines are T+2 (Starter) and T+1 (Business/Enterprise) on business days.</li><li>Transaction fees (MDR) are deducted before settlement as per your plan's fee schedule.</li></ul> },
  { n: '06', title: 'Chargebacks and Disputes', body: <p className="legal-p">You are responsible for maintaining sufficient records to defend against chargebacks. In the event of a chargeback, we may withhold the disputed amount from your settlement pending resolution. Excessive chargebacks may result in account suspension.</p> },
  { n: '07', title: 'Limitation of Liability', body: <p className="legal-p">A1XPAY shall not be liable for indirect, incidental, consequential, or punitive damages. Our maximum liability in any case shall not exceed the fees paid by you in the 30 days preceding the claim.</p> },
  { n: '08', title: 'Termination', body: <p className="legal-p">We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, or fail to comply with applicable regulations. You may close your account at any time with 30 days written notice.</p> },
  { n: '09', title: 'Governing Law', body: <p className="legal-p">These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Delhi, India.</p> },
  { n: '10', title: 'Contact', body: <p className="legal-p">For any queries regarding these Terms, email us at legal@a1xpay.com.</p> },
]

export default function TermsConditions() {
  return (
    <>
      <style>{legalCss}</style>
      <div className="legal-root">
        <Navbar />
        <section className="legal-hero">
          <div className="legal-container">
            <div className="legal-hero-grid">
              <motion.div className="legal-hero-text" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
                <div className="legal-hero-icon"><FileText size={26} /></div>
                <span className="legal-sub">Legal Document</span>
                <h1 className="legal-hero-h1">Terms &amp; Conditions</h1>
                <p className="legal-hero-p">By using A1XPAY services you agree to these terms. Please read them carefully before creating your account.</p>
                <span className="legal-hero-date">Last updated: October 1, 2024</span>
              </motion.div>
              <motion.div className="legal-hero-visual" initial={{ opacity: 0, x: 40, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                <div className="legal-hero-illus">
                  <img src={`${B}/card-left-Illus.png`} alt="Terms illustration" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        <section className="legal-body">
          <div className="legal-body-container">
            {sections.map((s, i) => (
              <motion.div key={s.n} className="legal-section-card"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.04 }}>
                <div className="legal-h2-wrap">
                  <div className="legal-h2-num">{s.n}</div>
                  <h2 className="legal-h2">{s.title}</h2>
                </div>
                {s.body}
              </motion.div>
            ))}
            <div className="legal-cta">
              <div className="legal-cta-text">
                <div className="legal-cta-h">Questions about our Terms?</div>
                <p className="legal-cta-p">Our legal team is happy to clarify any part of our terms.</p>
                <Link to="/contact-us" className="legal-cta-btn"><Mail size={15} /> Contact us</Link>
              </div>
              <motion.div className="legal-cta-illu" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <img src={`${B}/get-start.png`} alt="Get started" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mid feature section */}
        <section className="legal-feat">
          <div className="legal-container">
            <div className="legal-feat-grid">
              <motion.div className="legal-feat-img" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.65 }}>
                <img src={`${B}/about-img-3.png`} alt="Permitted use illustration" />
              </motion.div>
              <motion.div className="legal-feat-text" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: 0.1 }}>
                <div className="legal-head-circle"><Users size={24} color="#fff" /></div>
                <span className="legal-sub">Permitted Use</span>
                <h2 className="legal-feat-h2">Built for legitimate Indian businesses</h2>
                <p className="legal-feat-p">A1XPAY services are exclusively available for lawful business use by registered Indian entities and individuals aged 18+.</p>
                <ul className="legal-feat-list">
                  <li>E-commerce and retail payments</li>
                  <li>SaaS and subscription billing</li>
                  <li>Professional services & freelancers</li>
                  <li>All RBI-approved business categories</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
